import React, { useEffect, useState, useRef } from "react";
import { getPublicaciones, createPublicacion, updatePublicacion, deletePublicacion } from "../api/publicaciones.js";
import { getRestaurantes } from "../api/restaurantes.js";
import InputGroup from "../components/InputGroup";
import SortableHeader from "../components/SortableHeader";
import { useSortableData } from "../hooks/useSortableData.js";
import { usePagination } from "../hooks/usePagination.js";
import { useAuth } from "../context/AuthContext.js";
import Pagination from "../components/Pagination";

const PublicacionesPage = () => {
    const { usuario: usuarioLogueado } = useAuth();
    const [publicaciones, setPublicaciones] = useState([]);
    const [publicacion, setPublicacion] = useState({
        titulo: "",
        descripcion: "",
        imagen: null,
        fecha_inicio: "",
        fecha_fin: "",
        estado: "activo",
        id_restaurante: "",
    });
    const [filteredPublicaciones, setFilteredPublicaciones] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [restaurantes, setRestaurantes] = useState([]);
    const inputRefs = useRef({});
    const { sortedItems: sortedPublicaciones, sortConfig, setSortConfig } = useSortableData(filteredPublicaciones);
    // Hook de paginación
    const { currentPage, setCurrentPage, paginatedItems: currentPublicaciones, totalPages, itemsPerPage } = usePagination(sortedPublicaciones, 20);
    const [errors, setErrors] = useState({});
    const [editMode, setEditMode] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [imagePreview, setImagePreview] = useState(null);

    // Cargar restaurantes
    useEffect(() => {
        const loadRestaurantes = async () => {
            try {
                const data = await getRestaurantes();
                setRestaurantes(Array.isArray(data) ? data : data.restaurantes || []);
            } catch (err) {
                console.error("Error al cargar restaurantes:", err);
            }
        };
        loadRestaurantes();
    }, []);

    // Cargar publicaciones
    const loadPublicaciones = async () => {
        try {
            const data = await getPublicaciones();
            const lista = Array.isArray(data) ? data : data.publicaciones || [];
            setPublicaciones(lista);
            setFilteredPublicaciones(lista);
        } catch (err) {
            console.error("Error al cargar publicaciones:", err);
        }
    };

    useEffect(() => {
        loadPublicaciones();
    }, []);

    // Filtro
    useEffect(() => {
        const filtered = publicaciones.filter((p) =>
            Object.values(p).some((val) =>
                String(val).toLowerCase().includes(searchTerm.toLowerCase())
            )
        );
        setFilteredPublicaciones(filtered);
    }, [searchTerm, publicaciones]);



    const handleChange = (e) => {
        const { name, value, files } = e.target;

        if (name === "imagen") {
            const file = files[0];
            if (file) {
                setPublicacion({ ...publicacion, imagen: file });
                // Crear una URL temporal para previsualización
                const previewURL = URL.createObjectURL(file);
                setImagePreview(previewURL);
            }
        } else {
            setPublicacion({ ...publicacion, [name]: value });
        }
    };

    const resetForm = () => {
        setPublicacion({ 
            titulo: "", 
            descripcion: "", 
            imagen: null,
            fecha_inicio: "",
            fecha_fin: "",
            estado: "activo", 
            id_restaurante: usuarioLogueado?.isSuperAdmin ? "" : usuarioLogueado?.id_restaurante || "" 
        });
        setImagePreview(null);
        setErrors({});
        setEditMode(false);
    };

    const validatePublicacion = (data) => {
        const newErrors = {};
        if (!data.titulo || data.titulo.trim() === "")
            newErrors.titulo = "Ingrese un título.";
        if (!data.descripcion || data.descripcion.trim() === "")
            newErrors.descripcion = "Ingrese una descripción.";
        if (!data.fecha_inicio)
            newErrors.fecha_inicio = "Seleccione una fecha de inicio.";
        if (!data.fecha_fin)
            newErrors.fecha_fin = "Seleccione una fecha de fin.";
        if (data.fecha_inicio && data.fecha_fin && new Date(data.fecha_inicio) > new Date(data.fecha_fin))
            newErrors.fecha_fin = "La fecha de fin debe ser posterior a la fecha de inicio.";
        if (!data.id_restaurante)
            newErrors.id_restaurante = "Seleccione un restaurante.";
        return newErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const validationErrors = validatePublicacion(publicacion);
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            const firstErrorField = Object.keys(validationErrors)[0];
            const fieldRef = inputRefs.current[firstErrorField];
            if (fieldRef?.scrollIntoView) {
                fieldRef.scrollIntoView({ behavior: "smooth", block: "center" });
                fieldRef.focus();
            }
            return;
        }

        try {
            const publicacionData = { ...publicacion };
            
            // Si no es SuperAdmin, asignar automáticamente el id_restaurante
            if (!usuarioLogueado?.isSuperAdmin) {
                publicacionData.id_restaurante = usuarioLogueado?.id_restaurante;
            }
            
            if (editMode) {
                await updatePublicacion(publicacion.id_publicacion, publicacionData);
                alert("Publicación actualizada correctamente");
            } else {
                await createPublicacion(publicacionData);
                alert("Publicación creada correctamente");
            }

            resetForm();
            setModalOpen(false);
            loadPublicaciones();
        } catch (err) {
            console.error("Error del backend:", err.response?.data || err.message);
            const backendMsg =
                err.response?.data?.error ||
                err.response?.data?.message ||
                "Error al guardar la publicación.";
            setErrors({ general: backendMsg });
            alert(backendMsg);
        }
    };

    const handleEdit = (p) => {
        setPublicacion(p);
        // Si la publicación tiene imagen, mostrarla como previsualización
        if (p.imagen) {
            const imageUrl = p.imagen.startsWith("http")
                ? p.imagen
                : `${process.env.REACT_APP_API_URL}${p.imagen}`;
            setImagePreview(imageUrl);
        } else {
            setImagePreview(null);
        }
        setEditMode(true);
        setModalOpen(true);
    };

    const handleDelete = async (id) => {
        if (window.confirm("¿Seguro que deseas eliminar esta publicación?")) {
            await deletePublicacion(id);
            loadPublicaciones();
        }
    };

    return (
        <div className="p-6 max-w-[90rem] mx-auto">
            <h1 className="text-3xl font-bold mb-6 text-center">Gestión de Publicaciones</h1>

            {/* Buscador + botón */}
            <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
                <input type="text" placeholder="🔍 Buscar..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="border p-2 rounded w-full md:w-1/2" />
                <button onClick={() => { resetForm(); setModalOpen(true); }} className="bg-green-600 text-white px-6 py-2 rounded hover:bg-green-700" > ➕ Nueva Publicación </button>
            </div>

            {/* Tabla */}
            <div className="overflow-x-auto bg-white shadow-md rounded-xl w-full">
                <table className="min-w-full text-sm text-left divide-y divide-gray-200">
                    <thead className="bg-gray-100 text-gray-700 font-semibold">
                        <tr>
                            <SortableHeader label="N°" sortKey="id_publicacion" sortConfig={sortConfig} setSortConfig={setSortConfig} className="px-4 py-2" />
                            <SortableHeader label="Título" sortKey="titulo" sortConfig={sortConfig} setSortConfig={setSortConfig} className="px-4 py-2" />
                            <th className="px-4 py-2">Imagen</th>
                            <SortableHeader label="Fecha Inicio" sortKey="fecha_inicio" sortConfig={sortConfig} setSortConfig={setSortConfig} className="px-4 py-2" />
                            <SortableHeader label="Fecha Fin" sortKey="fecha_fin" sortConfig={sortConfig} setSortConfig={setSortConfig} className="px-4 py-2" />
                            <SortableHeader label="Estado" sortKey="estado" sortConfig={sortConfig} setSortConfig={setSortConfig} className="px-4 py-2" />
                            {usuarioLogueado?.isSuperAdmin && (
                                <SortableHeader label="Restaurante" sortKey="restaurante.nombre_comercial" sortConfig={sortConfig} setSortConfig={setSortConfig} className="px-4 py-2" />
                            )}
                            <th className="px-4 py-2 text-center">Acciones</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                        {currentPublicaciones.length > 0 ? (
                            currentPublicaciones.map((p, idx) => (
                                <tr key={p.id_publicacion} className="hover:bg-gray-50">
                                    <td className="px-4 py-2">{(currentPage - 1) * itemsPerPage + idx + 1}</td>
                                    <td className="px-4 py-2">{p.titulo}</td>
                                    <td className="px-4 py-2">
                                        {p.imagen ? (
                                            <img 
                                                src={p.imagen.startsWith("http") ? p.imagen : `${process.env.REACT_APP_API_URL}${p.imagen}`} 
                                                alt={p.titulo} 
                                                className="w-16 h-16 object-cover rounded" 
                                            />
                                        ) : (
                                            <span className="text-gray-400">Sin imagen</span>
                                        )}
                                    </td>
                                    <td className="px-4 py-2">{p.fecha_inicio?.split("T")[0]}</td>
                                    <td className="px-4 py-2">{p.fecha_fin?.split("T")[0]}</td>
                                    <td className="px-4 py-2 capitalize">{p.estado}</td>
                                    {usuarioLogueado?.isSuperAdmin && (
                                        <td className="px-4 py-2">
                                            <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-medium">
                                                {p.restaurante?.nombre_comercial || "Sin restaurante"}
                                            </span>
                                        </td>
                                    )}
                                    <td className="px-4 py-2 text-center flex justify-center gap-2">
                                        <button className="bg-yellow-400 text-white px-3 py-1 rounded hover:bg-yellow-500" onClick={() => handleEdit(p)} > Editar </button>
                                        <button className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600" onClick={() => handleDelete(p.id_publicacion)} > Eliminar </button>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={usuarioLogueado?.isSuperAdmin ? "8" : "7"} className="text-center py-4 text-gray-500"> No hay publicaciones registradas </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Paginación */}
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />

            {/* Modal Crear / Editar */}
            {modalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-xl shadow-lg w-[70%] max-h-[90vh] overflow-y-auto px-10 py-6 relative">
                        <button onClick={() => setModalOpen(false)} className="absolute top-2 right-3 text-gray-600 hover:text-red-500 text-xl"> ✖ </button>
                        <h2 className="text-2xl font-semibold mb-6 text-center"> {editMode ? "Editar Publicación" : "Nueva Publicación"} </h2>

                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <InputGroup label="Título" error={errors.titulo}> <input name="titulo" value={publicacion.titulo} onChange={handleChange} placeholder="Ej: Promoción de verano" className="input-base" ref={(el) => (inputRefs.current["titulo"] = el)} /> </InputGroup>
                                
                                {/* Campo Restaurante - Solo visible para SuperAdmin */}
                                {usuarioLogueado?.isSuperAdmin && (
                                    <InputGroup label="Restaurante" error={errors.id_restaurante}>
                                        <select name="id_restaurante" value={publicacion.id_restaurante} onChange={handleChange} className="input-base" ref={(el) => (inputRefs.current["id_restaurante"] = el)}>
                                            <option value="">Seleccione un restaurante</option>
                                            {restaurantes.map((r) => (
                                                <option key={r.id_restaurante} value={r.id_restaurante}>
                                                    {r.nombre_comercial}
                                                </option>
                                            ))}
                                        </select>
                                    </InputGroup>
                                )}
                                
                                <InputGroup label="Fecha Inicio" error={errors.fecha_inicio}> <input type="date" name="fecha_inicio" value={publicacion.fecha_inicio?.split("T")[0] || ""} onChange={handleChange} className="input-base" ref={(el) => (inputRefs.current["fecha_inicio"] = el)} /> </InputGroup>
                                <InputGroup label="Fecha Fin" error={errors.fecha_fin}> <input type="date" name="fecha_fin" value={publicacion.fecha_fin?.split("T")[0] || ""} onChange={handleChange} className="input-base" ref={(el) => (inputRefs.current["fecha_fin"] = el)} /> </InputGroup>
                                <InputGroup label="Estado" error={errors.estado}> <select name="estado" value={publicacion.estado} onChange={handleChange} className="input-base" > <option value="activo">Activo</option> <option value="inactivo">Inactivo</option> </select> </InputGroup>
                            </div>

                            <InputGroup label="Descripción" error={errors.descripcion}> <textarea name="descripcion" value={publicacion.descripcion} onChange={handleChange} placeholder="Descripción de la publicación" className="input-base" rows="4" ref={(el) => (inputRefs.current["descripcion"] = el)} /> </InputGroup>

                            <InputGroup label="Imagen">
                                <input 
                                    type="file" 
                                    name="imagen" 
                                    accept="image/*" 
                                    onChange={handleChange} 
                                    className="input-base" 
                                />
                                {(imagePreview || publicacion.imagen) && (
                                    <img
                                        src={imagePreview || `${process.env.REACT_APP_API_URL}${publicacion.imagen}`}
                                        alt="Previsualización"
                                        className="w-32 h-32 mt-3 object-cover rounded border"
                                    />
                                )}
                            </InputGroup>

                            {errors.general && ( <p className="text-red-600 text-sm text-center">{errors.general}</p> )}

                            <div className="text-center">
                                <button type="submit" className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700"> {editMode ? "Actualizar" : "Guardar"} </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PublicacionesPage;
